import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  isWithinBounds,
  useGetFootprintingDairyHerdDetails,
  useGetFootprintingDairyHerdSize,
  useGetFootprintingDairyPrimaryDataById,
  useMutateFootprintingDairyPrimaryData,
  useTranslation,
} from "utils";

import { ENUM_FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE, FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE } from "models";

import { Card, Flex, Form, FormInstance, InputNumber, KlimRadioGroup, PageSpin } from "components";

interface DairyGeneralProps {
  year: number;
  farmId: string;
  primaryDataId?: string;
  onComplete?: () => void;
  readOnly?: boolean;
}

export const FeedPastureForm = ({ primaryDataId, readOnly, farmId, year }: DairyGeneralProps) => {
  const { t } = useTranslation("footprintingDairy");
  const formRef = useRef<FormInstance>(null);

  const [cowsInPastureActive, setCowsInPastureActive] = useState<ENUM_FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE | null>(null);
  const [cowsInPasture, setCowsInPasture] = useState<number | null>(null);
  const [daysInPasturePerYear, setDaysInPasturePerYear] = useState<number | null>(null);
  const [avgHoursInPasturePerDay, setAvgHoursInPasturePerDay] = useState<number | null>(null);
  const [totalHerdSize, setTotalHerdSize] = useState<number>(0);

  const totalTimeInPasture = (daysInPasturePerYear ?? 0) * (avgHoursInPasturePerDay ?? 0);

  const { data: dairyPrimaryDataQuery, isLoading } = useGetFootprintingDairyPrimaryDataById(primaryDataId ?? "");
  const { data: herdSize } = useGetFootprintingDairyHerdSize(primaryDataId || "", { enabled: !!primaryDataId });
  const { data: calvesAmount } = useGetFootprintingDairyHerdDetails(primaryDataId || "", { enabled: !!primaryDataId });

  const { mutate: mutatePastureData } = useMutateFootprintingDairyPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      cowsInPastureActive,
      cowsInPasture,
      daysInPasturePerYear,
      avgHoursInPasturePerDay,
    },
    primaryDataId ?? ""
  );

  //   Save on unmount
  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutatePastureData();
      }
    },
    [isLoading, mutatePastureData, readOnly]
  );

  useEffect(() => {
    let total = 0;

    if (herdSize && herdSize.length > 0) {
      total += herdSize.reduce((sum, herd) => sum + (herd.cowCount || 0), 0);
    }

    if (calvesAmount) {
      total += calvesAmount.calvesAmount || 0;
    }

    setTotalHerdSize(total);
  }, [herdSize, calvesAmount]);

  //   Load existing data from sold cows table
  useEffect(() => {
    if (dairyPrimaryDataQuery) {
      setCowsInPastureActive(dairyPrimaryDataQuery.cowsInPastureActive ?? null);
      setCowsInPasture(dairyPrimaryDataQuery.cowsInPasture ?? null);
      setDaysInPasturePerYear(dairyPrimaryDataQuery.daysInPasturePerYear ?? null);
      setAvgHoursInPasturePerDay(dairyPrimaryDataQuery.avgHoursInPasturePerDay ?? null);

      formRef.current?.setFieldsValue({
        cowsInPasture: dairyPrimaryDataQuery.cowsInPasture ?? null,
        daysInPasturePerYear: dairyPrimaryDataQuery.daysInPasturePerYear ?? null,
        avgHoursInPasturePerDay: dairyPrimaryDataQuery.avgHoursInPasturePerDay ?? null,
      });

      const fieldsToValidate = (["cowsInPasture", "daysInPasturePerYear", "avgHoursInPasturePerDay"] as const).some(
        (field) => dairyPrimaryDataQuery[field] !== null && dairyPrimaryDataQuery[field] !== undefined
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [dairyPrimaryDataQuery]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <div>
      <h1 className="center margin-bottom-200 padding-left-75 padding-right-75 margin-left-50 margin-right-50">{t("dairyFeedPastureForm.title")}</h1>
      <Card className="padding-200 margin-top-100">
        <Flex align="center" gap={16} justify="space-between">
          <span style={{ maxWidth: "49%" }}>
            {t("dairyFeedPastureForm.cowPastureRadioQuestion")}
            <span className="required-asterisk">*</span>
          </span>
          <KlimRadioGroup
            required
            vertical={false}
            disabled={readOnly}
            type="tertiary"
            size="large"
            dataTestId="footprinting-dairy-herd-cow-weight-radio"
            onChange={({ target }) => {
              setCowsInPastureActive(target.value);
            }}
            value={cowsInPastureActive}
            optionType="button"
            options={[FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE.YES, FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE.NO].map((cowsInPastureActive) => ({
              label: t(`dairyFeedPastureForm.cowPastureOptions.${cowsInPastureActive}`),
              value: cowsInPastureActive,
            }))}
          />
        </Flex>

        {cowsInPastureActive === FARM_FOOTPRINTING_DAIRY_COWS_IN_PASTURE.YES && (
          <>
            <Form ref={formRef}>
              <Flex vertical gap={24} className="margin-top-150">
                <Form.Item>
                  <InputNumber
                    disabled={readOnly}
                    name="cowsInPasture"
                    dataTestId="footprinting-feed-cows-in-pasture-input"
                    label={t("dairyFeedPastureForm.question.cowsInPastureInput")}
                    suffix={t("dairyFeedPastureForm.question.cowsInPastureSuffix")}
                    value={cowsInPasture}
                    initialValue={cowsInPasture}
                    onChange={(v) => setCowsInPasture(v)}
                    validateFirst
                    rules={[
                      { required: true },
                      { type: "number" },
                      {
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                            return Promise.reject(new Error(t("dairyFeedPastureForm.error.validNumberCows")));
                          }
                          if (isNullOrUndefined(v) || !isWithinBounds(v, 0, totalHerdSize)) {
                            return Promise.reject(new Error(t("dairyFeedPastureForm.error.validNumberCowsInHerd", { totalHerdSize })));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </Form.Item>

                <Form.Item>
                  <InputNumber
                    disabled={readOnly}
                    name="daysInPasturePerYear"
                    dataTestId="footprinting-feed-days-in-pasture-input"
                    label={t("dairyFeedPastureForm.question.daysInPastureInput")}
                    suffix={t("dairyFeedPastureForm.question.daysInPastureSuffix")}
                    value={daysInPasturePerYear}
                    initialValue={daysInPasturePerYear}
                    onChange={(v) => setDaysInPasturePerYear(v)}
                    validateFirst
                    rules={[
                      { required: true },
                      { type: "number" },
                      {
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || !isWithinBounds(v, 0, 365)) {
                            return Promise.reject(new Error(t("dairyFeedPastureForm.error.validNumberDays")));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </Form.Item>

                <Form.Item>
                  <InputNumber
                    disabled={readOnly}
                    name="avgHoursInPasturePerDay"
                    dataTestId="footprinting-feed-time-in-pasture-input"
                    label={t("dairyFeedPastureForm.question.avgHoursInPastureInput")}
                    suffix={t("dairyFeedPastureForm.question.avgHoursInPastureSuffix")}
                    value={avgHoursInPasturePerDay}
                    initialValue={avgHoursInPasturePerDay}
                    onChange={(v) => setAvgHoursInPasturePerDay(v)}
                    validateFirst
                    rules={[
                      { required: true },
                      { type: "number" },
                      {
                        validator: (_: unknown, v: number) => {
                          if (isNullOrUndefined(v) || !isWithinBounds(v, 0, 24)) {
                            return Promise.reject(new Error(t("dairyFeedPastureForm.error.validNumberHours")));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                  />
                </Form.Item>
              </Flex>
            </Form>
            <Flex className="dairy-display-information-card flex-center padding-100 margin-top-150">
              <p className="paragraph-s grey-600-text margin-0 margin-bottom-25">{t("dairyFeedPastureForm.feedPastureDisplayTitle")}</p>
              <h5 className="center">
                {totalTimeInPasture} {t("dairyFeedPastureForm.feedPastureDisplayUnit")}
              </h5>
            </Flex>
          </>
        )}
      </Card>
    </div>
  );
};
